import React, { useContext, useState, useCallback } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import {
  AccordionSummary as MuiAccordionSummary,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import EDIcon, { IconColor } from '../Icon/EDIcon';
import classNames from 'classnames';
import { GridContext } from '../../Features/GridContext';

interface IExpansionProps {
  expanded: boolean;
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    expanded: boolean
  ) => void;
  testId?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement> | undefined;
}

interface IProps {
  /** optional background color for the panel (passed to styles) */
  backgroundColor?: string;
  borderVisible?: boolean;
  disableDetailsPadding?: boolean;
  disabled?: boolean;
  expandIconColor?: IconColor;
  label?: React.ReactNode;
  children?: React.ReactNode | ComponentFunc;
  elevation?: number;
  changeSummaryStyleWhenExpanded?: boolean;
  subtleSummaryChange?: boolean;
  headerColor?: string;
  /**
   * If true, the expansion panel will be rendered in a more compact style
   */
  isCompact?: boolean;
  /**
   * If true, the expansion panel will start in an expanded state
   */
  startExpanded?: boolean;
  testId?: string;
  lightBackgroundColor?: boolean;
  //För att kunna lägga elipsis på ev lång text i panelens header
  overflowHidden?: boolean;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  detailsBackgroundColor?: string;
  /**
   * If true, the expansion panel will keep its content mounted even when not expanded
   */
  keepMounted?: boolean;
}

const styles = (
  theme: Theme,
  params: IProps,
  classes: Record<'expanded', string>
) =>
  ({
    headerColor: {
      backgroundColor: params.headerColor ? params.headerColor : 'inherit',
    },
    heading: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightRegular,
    },
    summary: {
      backgroundColor: theme.palette.secondary.dark,
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
    },
    expandIconWhite: {
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
    },
    lightBackgroundColor: {
      backgroundColor: theme.customProperties.errorComponent.accordionError,
    },
    customBackgroundColor: {
      backgroundColor: params.backgroundColor
        ? params.backgroundColor
        : 'inherit',
    },
    overflowHidden: {
      overflow: 'hidden',
    },
    expandIcon: {
      marginRight: theme.spacing(0),
    },
    expandIconWrapper: {
      padding: theme.spacing(1.5),
    },
    compactExpandIconWrapper: {
      padding: theme.spacing(0.5, 1.5),
    },
    positionedExpandIcon: {
      position: 'absolute',
      right: 0,
      top: `calc(50% - ${theme.spacing(3)})`,
    },
    summarySubtle: {
      backgroundColor: theme.customProperties.ActivityCardPanel.rootLightBg,
      margin: theme.spacing(1, 0),
      borderRadius: 2,
    },
    content: {
      '&.Mui-expanded': {
        margin: theme.spacing(1.75, 0),
      },
    },
    compactContent: {
      margin: theme.spacing(0.5, 0),
      '&.Mui-expanded': {
        margin: theme.spacing(1, 0),
      },
    },
    contentPadded: {
      padding: theme.spacing(0, 2),
    },
    detailsBackgroundColor: {
      backgroundColor: params.detailsBackgroundColor
        ? params.detailsBackgroundColor
        : 'inherit',
    },
    expanded: {},
    borderVisible: {
      border: `1px solid #E2E2E2`,
      borderRadius: '6px',
    },

    /*Styles applied to the root element.*/
    root: {
      width: '100%',
      boxShadow: 'none',
    },

    /*Styles applied to the summary root element.*/
    summaryRoot: {
      '&:hover': {
        background: theme.customProperties.Fade.BlueGray.hover,
      },
      padding: theme.spacing(0),
      [`&.${classes.expanded}`]: {
        minHeight: theme.spacing(5),
        '&:hover': {
          background: theme.customProperties.Fade.BlueGray.hover,
        },
      },
    },
    compactSummaryRoot: {
      minHeight: theme.spacing(1),

      [`&.${classes.expanded}`]: {
        minHeight: theme.spacing(1.5),
        '&:hover': {
          background: theme.customProperties.Fade.BlueGray.hover,
        },
      },
    },

    /*Styles applied to the root element.*/
    detailsRoot: {
      padding: params.disableDetailsPadding ? 0 : theme.spacing(1, 1, 2),
      display: 'flex',
      justifyContent: 'center',
    },
  } as const);

type ComponentFunc = () => React.ReactNode;

const evalChildren = (
  children: React.ReactNode | ComponentFunc,
  open: boolean,
  detailsRoot: string,
  details: string,
  detailsBackgroundColor?: string
) => {
  if (typeof children === 'function') {
    if (open) {
      return (
        <MuiAccordionDetails
          style={{ width: '100%' }}
          classes={{ root: detailsRoot }}
        >
          {(children as ComponentFunc)()}
        </MuiAccordionDetails>
      );
    } else {
      return null;
    }
  } else {
    return (
      <MuiAccordionDetails
        style={{ flexWrap: 'wrap' }}
        classes={{
          root: classNames(detailsRoot, {
            [details]: detailsBackgroundColor !== undefined,
          }),
        }}
      >
        {children || <div />}
      </MuiAccordionDetails>
    );
  }
};

const EDExpansionPanelBase = (props: IProps & IExpansionProps) => {
  let { label } = props;
  const classes = withStyles.getClasses(props);
  const gridContext = useContext(GridContext);
  if (typeof props.label === 'string') {
    // FIXME: Marcus: Denna typografi har ingen variant alls. Oklart var den används och hur.
    label = (
      <Typography className={classes.heading}>
        {props.label ? props.label : ''}
      </Typography>
    );
  }

  return (
    <MuiAccordion
      classes={{
        root: classNames(classes.root, {
          [classes.lightBackgroundColor]: props.lightBackgroundColor,
          [classes.customBackgroundColor]: props.backgroundColor !== undefined,
          [classes.borderVisible]: props.borderVisible,
        }),
      }}
      data-testid={props.testId}
      disabled={props.disabled}
      elevation={props.elevation}
      expanded={props.expanded}
      onChange={props.onChange}
      square
      slotProps={{
        transition: {
          mountOnEnter: !props.keepMounted,
          unmountOnExit: !props.keepMounted,
        },
      }}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <MuiAccordionSummary
        expandIcon={
          <EDIcon iconType="expand_more" color={props.expandIconColor} />
        }
        classes={{
          root: classNames(
            props.isCompact ? classes.compactSummaryRoot : classes.summaryRoot,
            {}
          ),
          expanded: classes.expanded,
          content: classNames(
            props.isCompact ? classes.compactContent : classes.content,
            {
              [classes.contentPadded]: gridContext.inGrid,
              [classes.overflowHidden]: props.overflowHidden,
            }
          ),
          expandIconWrapper: classNames(
            props.isCompact
              ? classes.compactExpandIconWrapper
              : classes.expandIconWrapper,
            {
              [classes.expandIconWhite]:
                props.changeSummaryStyleWhenExpanded && props.expanded,
              [classes.positionedExpandIcon]: gridContext.inGrid,
            }
          ),
        }}
        className={classNames({
          [classes.summary]:
            props.changeSummaryStyleWhenExpanded && props.expanded,
          [classes.summarySubtle]: props.subtleSummaryChange && props.expanded,
          [classes.headerColor]: props.headerColor !== undefined,
        })}
      >
        {label}
      </MuiAccordionSummary>
      {evalChildren(
        props.children,
        props.expanded,
        classes.detailsRoot,
        classes.detailsBackgroundColor,
        props.detailsBackgroundColor
      )}
    </MuiAccordion>
  );
};

// Om du vill hantera expanded och onChange utifrån
export const EDControlledExpansionPanel = withStyles(
  EDExpansionPanelBase,
  styles,
  { uniqId: '07dcf15b-8f7c-4f74-a5d1-c198a3ad89b8' }
);

/**
 * En expansionspanel visar en innehållspanels rubrik.
 Vid klick expanderas panelen.
 */
const EDExpansionPanel = (props: IProps) => {
  const [expanded, setExpanded] = useState(props.startExpanded ?? false);
  const onChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, newExpanded: boolean) => {
      setExpanded(newExpanded);
    },
    []
  );
  return (
    <EDControlledExpansionPanel
      expanded={expanded}
      onChange={onChange}
      {...props}
    />
  );
};

export default EDExpansionPanel;
